import React, { SyntheticEvent, useCallback, useEffect, useState } from 'react';

import { Helmet } from 'react-helmet';
import { useHistory } from 'react-router';

import { Container, Stack, Tab, Tabs } from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';
import { preRegisterSpotlightRoutes } from '~/components/Common/Spotlight/SpotlightProvider';

import featureToggle from '~/utils/featureToggle';

import MakeMeAnOfferButton from '../../components/MakeMeAnOffer/MakeMeAnOfferButton';
import AccommodationProperties from '../Properties/AccommodationProperties';

export enum AccommodationTabs {
  PROPERTIES = 'properties',
  UNMAPPED_SUPPLIERS = 'unmapped_suppliers',
  POTENTIAL_MAPPINGS = 'potential_mappings',
  FACILITIES = 'facilities',
  AMENITIES = 'amenities',
  MMAO_OFFERS = 'mmao_offers',
}

preRegisterSpotlightRoutes([
  {
    path: `/accommodation/properties#${AccommodationTabs.PROPERTIES}`,
    label: 'Accommodation / Properties',
    section: 'Accommodation',
  },
  {
    path: `/accommodation/properties#${AccommodationTabs.POTENTIAL_MAPPINGS}`,
    label: 'Accommodation / Potential Mappings',
    section: 'Accommodation',
  },
  {
    path: `/accommodation/properties#${AccommodationTabs.UNMAPPED_SUPPLIERS}`,
    label: 'Accommodation / Unmapped Suppliers',
    section: 'Accommodation',
  },
  {
    path: `/accommodation/properties#${AccommodationTabs.FACILITIES}`,
    label: 'Accommodation / Facilities',
    section: 'Accommodation',
  },
  {
    path: `/accommodation/properties#${AccommodationTabs.AMENITIES}`,
    label: 'Accommodation / Amenities',
    section: 'Accommodation',
  },
]);

export default function AccommodationHomePage() {
  const [selectedTab, setSelectedTab] = useState(
    (window.location.hash?.replace('#', '') as AccommodationTabs) ?? AccommodationTabs.PROPERTIES,
  );
  const history = useHistory();

  const isMmaoEnabled = featureToggle.availableToShow('MAKE_ME_AN_OFFER_ENABLED');
  const isMakeMeAnOfferButtonVisible = isMmaoEnabled;

  useEffect(() => {
    if (window.location.hash?.replace('#', '') === '') {
      window.location.hash = AccommodationTabs.PROPERTIES;
      setSelectedTab(AccommodationTabs.PROPERTIES);
    }
  }, []);

  const handleTabChange = useCallback(
    (event: SyntheticEvent, newValue: AccommodationTabs) => {
      setSelectedTab(newValue);
      history.push({
        search: '',
      });
      window.location.hash = newValue;
    },
    [history],
  );

  return (
    <Container maxWidth="xl">
      <Helmet>
        <title>Accommodation - Properties</title>
      </Helmet>
      <PageHeader title="Accommodation - Properties">
        {isMakeMeAnOfferButtonVisible && <MakeMeAnOfferButton />}
      </PageHeader>
      <Stack direction="row" spacing={2} justifyContent="space-between">
        <Tabs sx={{ mb: 2 }} value={selectedTab} onChange={handleTabChange}>
          <Tab key={AccommodationTabs.PROPERTIES} value={AccommodationTabs.PROPERTIES} label="Properties" />
          <Tab
            key={AccommodationTabs.POTENTIAL_MAPPINGS}
            value={AccommodationTabs.POTENTIAL_MAPPINGS}
            label="Potential Mappings"
          />
          <Tab
            key={AccommodationTabs.UNMAPPED_SUPPLIERS}
            value={AccommodationTabs.UNMAPPED_SUPPLIERS}
            label="Unmapped Suppliers"
          />
          <Tab key={AccommodationTabs.FACILITIES} value={AccommodationTabs.FACILITIES} label="Facilities" />
          <Tab key={AccommodationTabs.AMENITIES} value={AccommodationTabs.AMENITIES} label="Amenities" />
          {isMmaoEnabled && (
            <Tab key={AccommodationTabs.MMAO_OFFERS} value={AccommodationTabs.MMAO_OFFERS} label="MMAO Offers" />
          )}
        </Tabs>
      </Stack>
      <AccommodationProperties selectedTab={selectedTab} />
    </Container>
  );
}
